import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeft } from 'lucide-react';
import { NavGroupList } from './NavGroupList';
import { SignedInLabel } from './SignedInLabel';
import type { NavState } from './useNavTree';

interface SidebarProps {
  nav: NavState;
  isCollapsed: boolean;
  onToggle: () => void;
}

/**
 * GEN-04-04 (Angela, 22 Oct 2026, design N1) — the white panel beside the icon
 * rail: the open top menu's name in large bold, 'Signed in: <outlet>' under it
 * (GEN-01-03), then that menu's second and third levels (the top menus are in
 * the rail and never repeated here). 184 px wide (narrowed by Angela, 22 Oct 2026), no icons, no connecting
 * lines; the current page alone has a light pink background. Groups open one at a time
 * (GEN-04-17) — the rendering is shared with the narrow-screen Menu
 * (NavGroupList).
 *
 * Collapsed, it is a slim strip whose open button is always visible; the
 * choice is remembered per user until sign-out (AppLayout). It stays at every
 * window width (GEN-04-07).
 */
export function Sidebar({ nav, isCollapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation('nav');
  const { activeTop, isActive } = nav;
  const groups = activeTop?.children ?? [];

  // The collapsed strip: the open button and nothing else (GEN-04-04 (7)).
  if (isCollapsed) {
    return (
      <aside
        data-testid="sidebar-desktop"
        data-collapsed="true"
        className="flex w-10 flex-shrink-0 flex-col items-center border-r border-[#EEEAF3] bg-white pt-3"
      >
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center justify-center rounded-md p-1.5 text-[#535353] transition-colors hover:bg-[#F7F7F7] hover:text-[#313131]"
          title={t('expandSidebar')}
          aria-label={t('expandSidebar')}
          data-testid="sidebar-expand"
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      </aside>
    );
  }

  return (
    <aside
      data-testid="sidebar-desktop"
      className="flex w-[184px] flex-shrink-0 flex-col border-r border-[#EEEAF3] bg-white"
    >
      {/* Top: the open menu's name, large + the collapse button; the sign-in's outlet under it. */}
      <div className="flex items-start justify-between pl-3 pr-1 pt-4">
        <span
          className="truncate text-[17px] font-extrabold text-[#1E1B2E]"
          data-testid="sidebar-menu-name"
        >
          {activeTop ? t(activeTop.labelKey) : ''}
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center justify-center rounded-md p-1.5 text-[#535353] transition-colors hover:bg-[#F7F7F7] hover:text-[#313131]"
          title={t('collapseSidebar')}
          aria-label={t('collapseSidebar')}
          data-testid="sidebar-collapse"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Angela, 24 Oct 2026: a clear gap between the Welcome line and the
          menu list under it, on every page. */}
      <div className="px-3 pb-5 pt-0.5">
        <SignedInLabel />
      </div>

      {/* GEN-04-04 (6): a long menu scrolls inside the sidebar. */}
      <nav
        className="flex-1 space-y-0 overflow-y-auto px-1 pb-4"
        aria-label={activeTop ? t(activeTop.labelKey) : undefined}
      >
        <NavGroupList groups={groups} isActive={isActive} />
      </nav>
    </aside>
  );
}
